<template lang="pug">
  div(
    style="height: 100%; width: 100%;")
    div(
      ref="container"
      style="translate(0px, 0px); width: 100%; height: 100%")
      div.fill-height(
        v-for="({ image, title, text }, i) in screens"
        style="position: absolute; height: 100%; width: 100%")
        div.pa-8(style="height: 50%")
          template(v-if="Array.isArray(image)")
            component(style="width: 50%" :is="image[0]")
            component(style="width: 50%" :is="image[1]")
          component(
            v-else
            :is="image")
        div.d-flex.justify-center.align-center.text-center(style="height: 45%;")
          div
            h2.title.px-8(v-if="title") {{ title }}
            p.mt-4.mx-auto(style="width: 284px") {{ text }}
    self-dots(
      :length="screens.length"
      :progress="progress")
</template>

<script>
import { ref, onMounted, reactive, toRefs, onUnmounted, computed, watch } from '@vue/composition-api'
import Hammer from 'hammerjs'
import { gsap, Sine } from 'gsap'
import { clamp } from 'lodash'
import SelfDots from './dots/'
import useWindow from '@iez/lib/use-window/'

export default {
  components: {
    SelfDots
  },

  props: {
    screens: Array,
    progressOverride: Number
  },

  setup (props) {
    const { screens } = props
    const container = ref(null)
    const state = reactive({
      step: 0,
      offset: 0,
      deltaX: 0,
      position: computed(() => state.offset + state.deltaX),
      progress: 0
    })

    let tween
    const setPosition = (el, x, transition) => {
      if (tween && tween.isActive()) {
        tween.kill()
      }
      const duration = 0.3
      tween = transition
        ? gsap.to(
          el,
          {
            duration,
            x,
            ease: Sine.easeOut,
            onUpdate: defaultOffset => {
              const distance = defaultOffset - x
              const offset = defaultOffset - tween.ratio * distance
              state.offset = offset
            },
            onUpdateParams: [el.getBoundingClientRect().x]
          }
        )
        : gsap.set(el, { x: state.offset + x })
    }
    const setEndPosition = (el, x) => {
      setPosition(el, x, true)
    }

    let hammer = null
    const window = useWindow()

    onMounted(() => {
      const el = container.value
      const children = Array.from(el.children)

      watch(
        () => props.progressOverride,
        progressOverride => {
          setEndPosition(el, -progressOverride * children[0].offsetWidth)
        }
      )

      watch(
        () => window,
        () => {
          el.style.height = `${el.parentNode.offsetHeight}px`
          children.forEach((child, i) => {
            setPosition(child, i * child.offsetWidth)
          })
        }, {
          deep: true,
          immediate: true
        }
      )

      watch(
        () => state.position,
        () => {
          state.progress = Math.abs(clamp(state.position / el.offsetWidth, -children.length + 1, 0))
        }, {
          immediate: true
        }
      )

      hammer = new Hammer.Manager(el, {
        inputClass: Hammer.TouchInput
      })
      hammer.add(new Hammer.Pan({ direction: Hammer.DIRECTION_HORIZONTAL }))
      el.style.transform = 'translate3d(0, 0, 0)'
      let previousIndex = 0
      hammer.on('pan panend panstart', ({ deltaX, deltaY, velocityX, type, direction }) => {
        state.deltaX = deltaX
        const index = Math.round(state.position / el.offsetWidth)
        if (type === 'panstart') {
          previousIndex = index
        } else if (type === 'panend') {
          const velocityThreshold = 0.6
          const absoluteVelocity = Math.abs(velocityX)

          setEndPosition(
            el,
            state.position > 0
              ? 0
              : state.position < -el.offsetWidth * (children.length - 1)
                ? -el.offsetWidth * (children.length - 1)
                : absoluteVelocity > velocityThreshold
                  ? (previousIndex + (velocityX / absoluteVelocity)) * el.offsetWidth
                  : el.offsetWidth * index
          )

          state.deltaX = 0
        } else {
          setPosition(el, deltaX)
        }
      })
    })

    onUnmounted(() => {
      hammer.destroy()
      tween && tween.kill()
    })

    return {
      container,
      ...toRefs(state)
    }
  }
}
</script>
